import { Extension } from '@tiptap/core';
import { Suggestion } from '@tiptap/suggestion';
import { TemplateVariableDefinitionDto } from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';
import { isBlockVariable } from '../templateVariableUtils.ts';
import { templateVariableDisplayNames } from '../templateVariableDisplayNames.ts';
import { useSuggestionStore } from '../suggestionStore.ts';

export interface VariableSuggestionOptions {
  getVariables: () => TemplateVariableDefinitionDto[];
}

const MAX_RESULTS = 10;

const matchesQuery = (
  variable: TemplateVariableDefinitionDto,
  query: string,
): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  const label = templateVariableDisplayNames[variable.key] ?? '';
  return (
    variable.key.toLowerCase().includes(q) || label.toLowerCase().includes(q)
  );
};

// Bridges `@tiptap/suggestion` plugin with the React-rendered SuggestionPopup.
// All popup UI/state lives in the store + `SuggestionPopup` component; this
// extension only owns the trigger detection and item filtering logic.
export const VariableSuggestion = Extension.create<VariableSuggestionOptions>({
  name: 'variableSuggestion',

  addOptions: () => ({ getVariables: () => [] }),

  addProseMirrorPlugins() {
    return [
      Suggestion<TemplateVariableDefinitionDto, TemplateVariableDefinitionDto>({
        editor: this.editor,
        char: '/',
        startOfLine: false,
        allowSpaces: false,

        items: ({ query }) =>
          this.options
            .getVariables()
            .filter((v) => matchesQuery(v, query))
            .slice(0, MAX_RESULTS),

        command: ({ editor, range, props: variable }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent({
              type: isBlockVariable(variable)
                ? 'templateVariableBlock'
                : 'templateVariable',
              attrs: { variable: variable.key },
            })
            .run();
        },

        render: () => ({
          onStart: (props) =>
            useSuggestionStore.getState().show({
              items: props.items,
              command: props.command,
              clientRect: props.clientRect?.() ?? null,
            }),
          onUpdate: (props) =>
            useSuggestionStore.getState().update({
              items: props.items,
              command: props.command,
              clientRect: props.clientRect?.() ?? null,
            }),
          onKeyDown: ({ event }) => {
            if (event.key === 'Escape') {
              useSuggestionStore.getState().hide();
              return true;
            }
            return useSuggestionStore.getState().handleKey(event);
          },
          onExit: () => useSuggestionStore.getState().hide(),
        }),
      }),
    ];
  },
});
